import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";
import _ from "lodash";

/** Opens Save As when supported; otherwise downloads to the default folder. */
export const savePdfWithDialog = async (pdfBytes, filename) => {
    if (typeof window.showSaveFilePicker === "function") {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: filename,
                types: [
                    {
                        description: "PDF Document",
                        accept: { "application/pdf": [".pdf"] },
                    },
                ],
            });
            const writable = await handle.createWritable();
            await writable.write(pdfBytes);
            await writable.close();
            return;
        } catch (err) {
            if (err?.name === "AbortError") {
                return;
            }
        }
    }

    download(pdfBytes, filename, "application/pdf");
};

// Helper function to convert number to words
const numberToWords = (num) => {
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];

    if (num === 0) return 'ZERO';

    const convert = (n) => {
        if (n < 10) return ones[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
        if (n < 1000) return ones[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 !== 0 ? ' AND ' + convert(n % 100) : '');
        if (n < 1000000) {
            const thousands = Math.floor(n / 1000);
            const remainder = n % 1000;
            return convert(thousands) + ' THOUSAND' + (remainder !== 0 ? ' ' + convert(remainder) : '');
        }
        if (n < 1000000000) {
            const millions = Math.floor(n / 1000000);
            const remainder = n % 1000000;
            return convert(millions) + ' MILLION' + (remainder !== 0 ? ' ' + convert(remainder) : '');
        }
        return 'NUMBER TOO LARGE';
    };

    const ringgit = Math.floor(num);
    const cents = Math.round((num - ringgit) * 100);

    let result = 'RINGGIT MALAYSIA ' + convert(ringgit);
    if (cents > 0) {
        result += ' CENTS AND ' + convert(cents);
    }
    result += ' ONLY';

    return result;
};

const getParticularsText = (material) => {
    const name =
        material.type ||
        material.activeColor?.type ||
        material.model ||
        material.name ||
        material.description ||
        "N/A";
    const measurements =
        material.measurements != null ? String(material.measurements).trim() : "";

    return measurements ? `${name} - ${measurements}` : name;
};

const createPurchaseOrder = async (original, options = {}) => {
    const { saveAs = false } = options;
    try {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        // Calculate total pages based on materials count
        const materials = original.materials || [];
        const itemsPerPage = 15;
        const totalPages = Math.max(1, Math.ceil(materials.length / itemsPerPage));

        // Create a new PDFDocument
        const pdfDoc = await PDFDocument.create();

        // Embed the fonts
        const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
        const timesRomanBoldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
        const timesRomanItalicFont = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);

        // Loop through pages
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            const page = pdfDoc.addPage();
            const fontSize = 20;
            const { height, width } = page.getSize();
            const standardFontSize = 9;

            // Logo
            const logoUrl =
                "https://ik.imagekit.io/dnddecpho/IHGroup/inhomelogo-new_80yctapLk.png";
            const logoBytes = await fetch(logoUrl).then((res) => res.arrayBuffer());
            const logoImage = await pdfDoc.embedPng(logoBytes);
            const jpgDims = logoImage.scale(95 / logoImage.width);
            page.drawImage(logoImage, {
                x: 35,
                y: height - 4 * fontSize,
                width: jpgDims.width,
                height: jpgDims.height,
            });

            // Company Header
            page.drawText(`INHOME GROUP SDN BHD`, {
                x: 140,
                y: height - 1.9 * fontSize,
                size: 18,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`(202101002744 (1403042K))`, {
                x: 380,
                y: height - 1.9 * fontSize,
                size: 11,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`No 3A & 5, Jln Kpb 18, Kawasan Perindustrian Balakong,`, {
                x: 140,
                y: height - 2.7 * fontSize,
                size: 12,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`43100 Seri Kembangan, Selangor`, {
                x: 140,
                y: height - 3.4 * fontSize,
                size: 12,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`T`, {
                x: 485,
                y: height - 4.1 * fontSize,
                size: 11,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`+6 017 775 3383`, {
                x: 495,
                y: height - 4.1 * fontSize,
                size: 11,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`E`, {
                x: 435,
                y: height - 4.8 * fontSize,
                size: 11,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`inhomegroup@gmail.com`, {
                x: 445,
                y: height - 4.8 * fontSize,
                size: 11,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`W`, {
                x: 450,
                y: height - 5.5 * fontSize,
                size: 11,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`www.inhome.com.my`, {
                x: 465,
                y: height - 5.5 * fontSize,
                size: 11,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            // PURCHASE ORDER Title
            page.drawText(`PURCHASE  ORDER`, {
                x: width / 2 - 80,
                y: height - 6.5 * fontSize,
                size: 14,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            // Separator line
            page.drawLine({
                start: { x: 30, y: height - 7.0 * fontSize },
                end: { x: width - 30, y: height - 7.0 * fontSize },
                color: rgb(0, 0, 0),
                thickness: 1,
            });

            // Left side - Supplier Information
            let leftY = 7.8;

            page.drawText(`To :`, {
                x: 40,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`${original.supplier?.name || original.userId?.name || 'N/A'}`, {
                x: 80,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            leftY += 0.7;

            page.drawText(`Address :`, {
                x: 40,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            // Address lines
            const addressLine1 = original.supplier?.address || original.lineOne || '';
            if (addressLine1) {
                page.drawText(`${addressLine1}`, {
                    x: 80,
                    y: height - leftY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                leftY += 0.6;
            }

            const addressLine2 = original.supplier?.address2 || original.lineTwo || '';
            if (addressLine2) {
                page.drawText(`${addressLine2}`, {
                    x: 80,
                    y: height - leftY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                leftY += 0.6;
            }

            const cityLine = `${original.supplier?.zip || original.zip || ''} ${original.supplier?.city || original.city || ''}`;
            if (cityLine.trim()) {
                page.drawText(cityLine.trim(), {
                    x: 80,
                    y: height - leftY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                leftY += 0.6;
            }

            const stateLine = `${original.supplier?.state || original.country || ''}`;
            if (stateLine.trim()) {
                page.drawText(stateLine.trim(), {
                    x: 80,
                    y: height - leftY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                leftY += 0.7;
            }

            page.drawText(`Email :`, {
                x: 40,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`${original.supplier?.email || original.userId?.email || 'N/A'}`, {
                x: 80,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            leftY += 0.7;

            page.drawText(`Tel :`, {
                x: 40,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(`${original.supplier?.phone || original.userId?.phoneNumber || 'N/A'}`, {
                x: 80,
                y: height - leftY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
  
            let rightY = 7.8;
            const rightX = 380;

            page.drawText(`Date  :`, {
                x: rightX,
                y: height - rightY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(
                `${moment(original.createdAt || new Date()).format("DD-MM-YYYY")}`,
                {
                    x: rightX + 60,
                    y: height - rightY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );

            rightY += 0.7;

            page.drawText(`No.  :`, {
                x: rightX,
                y: height - rightY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(
                `${original.poNumber || original.invoiceId || 'N/A'}`,
                {
                    x: rightX + 60,
                    y: height - rightY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );

            rightY += 0.7;

            page.drawText(`Ref.  :`, {
                x: rightX,
                y: height - rightY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(
                `${original.referenceNumber || ''}`,
                {
                    x: rightX + 60,
                    y: height - rightY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );

            rightY += 0.7;

            page.drawText(`Terms  :`, {
                x: rightX,
                y: height - rightY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(
                `${original.paymentTerms || 'Net 30 days'}`,
                {
                    x: rightX + 60,
                    y: height - rightY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );

            rightY += 0.7;

            page.drawText(`Status  :`, {
                x: rightX,
                y: height - rightY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText(
                `${original.status || ''}`,
                {
                    x: rightX + 60,
                    y: height - rightY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );

            // Table section
            const tableStartY = Math.max(leftY, rightY) + 1.5;

            // Table header line
            page.drawLine({
                start: { x: 30, y: height - tableStartY * fontSize },
                end: { x: width - 30, y: height - tableStartY * fontSize },
                color: rgb(0, 0, 0),
                thickness: 1,
            });

            // Table Headers
            let headerY = tableStartY + 0.6;

            page.drawText("NO", {
                x: 40,
                y: height - headerY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("PARTICULARS", {
                x: 80,
                y: height - headerY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("UOM", {
                x: 300,
                y: height - headerY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("QTY", {
                x: 350,
                y: height - headerY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("U/ Price (RM)", {
                x: 390,
                y: height - headerY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("Total (RM)", {
                x: 500,
                y: height - headerY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            headerY += 0.6;

            page.drawLine({
                start: { x: 30, y: height - headerY * fontSize },
                end: { x: width - 30, y: height - headerY * fontSize },
                color: rgb(0, 0, 0),
                thickness: 1,
            });

            // Materials data
            const startIndex = pageIndex * itemsPerPage;
            const endIndex = Math.min(startIndex + itemsPerPage, materials.length);
            const pageMaterials = materials.slice(startIndex, endIndex);

            let currentY = headerY + 0.7;

            pageMaterials.forEach((material, index) => {
                const itemNumber = startIndex + index + 1;
                const uom = material.uom || 'PCS';
                const quantity = material.amount || material.quantity || 0;
                const price = material.price || material.activeColor?.price || 0;
                const total = quantity * price;
                const particularsText = getParticularsText(material);

                page.drawText(`${itemNumber}`, {
                    x: 40,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                // Wrap text if too long
                const maxParticularsWidth = 35;
                const displayParticulars =
                    particularsText.length > maxParticularsWidth
                        ? particularsText.substring(0, maxParticularsWidth) + "..."
                        : particularsText;

                page.drawText(displayParticulars, {
                    x: 80,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText(uom, {
                    x: 300,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText(`${quantity}`, {
                    x: 350,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText(`${NumberFormatter.format(price)}`, {
                    x: 400,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText(`${NumberFormatter.format(total)}`, {
                    x: 490,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                currentY += 0.8;
            });

            // Only show totals on the last page
            if (pageIndex === totalPages - 1) {
                // Move to bottom area for totals
                currentY = 34;

                page.drawLine({
                    start: { x: 30, y: height - currentY * fontSize },
                    end: { x: width - 30, y: height - currentY * fontSize },
                    color: rgb(0, 0, 0),
                    thickness: 1,
                });

                currentY += 0.8;

                // Calculate grand total
                let grandTotal = 0;
                materials.forEach(material => {
                    const quantity = material.amount || material.quantity || 0;
                    const price = material.price || material.activeColor?.price || 0;
                    grandTotal += quantity * price;
                });

                // Total in words
                const totalInWords = numberToWords(grandTotal);
                
                page.drawText(totalInWords, {
                    x: 40,
                    y: height - currentY * fontSize,
                    size: 8,
                    font: timesRomanBoldFont,
                    color: rgb(0, 0, 0),
                });

                // Total amount box
                page.drawRectangle({
                    x: 470,
                    y: height - (currentY + 0.5) * fontSize,
                    width: 90,
                    height: 18,
                    borderColor: rgb(0, 0, 0),
                    borderWidth: 1,
                });

                page.drawText(`Total`, {
                    x: 480,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanBoldFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText(`${NumberFormatter.format(grandTotal)}`, {
                    x: 520,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanBoldFont,
                    color: rgb(0, 0, 0),
                });

                // Signature section
                currentY += 3;

                page.drawText(`Requested By ,`, {
                    x: 40,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText(`Approved By ,`, {
                    x: 380,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                currentY += 1.5;

                // Signature lines
                page.drawLine({
                    start: { x: 40, y: height - currentY * fontSize },
                    end: { x: 200, y: height - currentY * fontSize },
                    color: rgb(0, 0, 0),
                    thickness: 0.5,
                });

                page.drawLine({
                    start: { x: 380, y: height - currentY * fontSize },
                    end: { x: 540, y: height - currentY * fontSize },
                    color: rgb(0, 0, 0),
                    thickness: 0.5,
                });

                currentY += 0.6;

                page.drawText(`Authorised Signature`, {
                    x: 410,
                    y: height - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            }

            // Page number at bottom
            if (totalPages > 1) {
                page.drawText(`Page ${pageIndex + 1} of ${totalPages}`, {
                    x: width / 2 - 30,
                    y: 20,
                    size: 8,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            }
        }

        const pdfBytes = await pdfDoc.save();
        const filename = `PurchaseOrder-${original.poNumber || original.invoiceId || "PO"}.pdf`;

        if (saveAs) {
            await savePdfWithDialog(pdfBytes, filename);
        } else {
            download(pdfBytes, filename, "application/pdf");
        }
    } catch (err) {
        alert("Failed to generate Purchase Order");
        console.log(err);
    }
};

export default createPurchaseOrder;
